import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes of inactivity
const WARNING_MS = 14 * 60 * 1000; // warn 1 minute before expiry
const COUNTDOWN_SEC = 60;

const TutorInactivityWatcher = ({ onLogout }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SEC);
  const lastActivityRef = useRef(Date.now());
  const warningActiveRef = useRef(false);

  const performLogout = () => {
    toast.info('Sesión cerrada por inactividad. Vuelve a iniciar sesión para ver información actualizada.');
    onLogout();
  };

  const keepAlive = () => {
    lastActivityRef.current = Date.now();
    warningActiveRef.current = false;
    setShowWarning(false);
    setCountdown(COUNTDOWN_SEC);
  };

  // Listen for user activity (ignored while warning modal is up)
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const handleActivity = () => {
      if (!warningActiveRef.current) {
        lastActivityRef.current = Date.now();
      }
    };
    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, handleActivity));
  }, []);

  // Tick every 5s: decide whether to warn or logout
  useEffect(() => {
    const checkInterval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= TIMEOUT_MS) {
        performLogout();
      } else if (elapsed >= WARNING_MS && !warningActiveRef.current) {
        warningActiveRef.current = true;
        setShowWarning(true);
      }
    }, 5000);
    return () => clearInterval(checkInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown while warning modal is visible
  useEffect(() => {
    if (!showWarning) return;
    setCountdown(COUNTDOWN_SEC);
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(id);
          performLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWarning]);

  if (!showWarning) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center px-4" data-testid="inactivity-warning">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full text-center">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-2">¿Sigues ahí?</h2>
        <p className="text-sm text-slate-600 mb-4">
          Tu sesión se cerrará en <span className="font-bold text-amber-600" data-testid="inactivity-countdown">{countdown}</span> segundos por inactividad.
        </p>
        <button
          onClick={keepAlive}
          className="w-full py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
          data-testid="inactivity-keep-session"
        >
          Mantener sesión activa
        </button>
      </div>
    </div>
  );
};

export default TutorInactivityWatcher;
